export const GOOGLE_ANALYTICS_PUBLIC_MEASUREMENT_ID = 'G-ZYJPBPSLLQ';
export const GOOGLE_ANALYTICS_ADMIN_MEASUREMENT_ID = 'G-357LQTDLNY';
export const GOOGLE_ANALYTICS_MEASUREMENT_ID = GOOGLE_ANALYTICS_PUBLIC_MEASUREMENT_ID;

type GtagCommand = 'config' | 'event' | 'js';

type GtagFunction = (command: GtagCommand, target: string | Date, params?: Record<string, unknown>) => void;

type GoogleAnalyticsUserContext = {
    userId?: string | null;
    workspaceId?: string | null;
};

declare global {
    interface Window {
        dataLayer?: unknown[];
        gtag?: GtagFunction;
        __bustlyGaInitialized?: boolean;
        __bustlyGaContext?: {
            user_id?: string;
            workspace_id?: string;
        };
    }
}

function getOrCreateGtag(): GtagFunction | null {
    if (typeof window === 'undefined') {
        return null;
    }

    window.dataLayer = window.dataLayer || [];

    if (!window.gtag) {
        window.gtag = function gtag(command, target, params) {
            window.dataLayer?.push([command, target, params]);
        };
    }

    return window.gtag;
}

export function ensureGoogleAnalyticsInitialized(
    measurementId: string = GOOGLE_ANALYTICS_MEASUREMENT_ID
) {
    if (typeof window === 'undefined' || window.__bustlyGaInitialized) {
        return;
    }

    const gtag = getOrCreateGtag();
    if (!gtag) {
        return;
    }

    if (!document.querySelector(`script[src*="${measurementId}"]`)) {
        const script = document.createElement('script');
        script.async = true;
        script.src = `https://www.googletagmanager.com/gtag/js?id=${measurementId}`;
        document.head.appendChild(script);
    }

    gtag('js', new Date());
    gtag('config', measurementId);
    window.__bustlyGaInitialized = true;
}

export async function syncGoogleAnalyticsUserContext(
    context: GoogleAnalyticsUserContext | null,
    measurementId: string = GOOGLE_ANALYTICS_MEASUREMENT_ID
) {
    if (typeof window === 'undefined') {
        return;
    }

    ensureGoogleAnalyticsInitialized(measurementId);

    const gtag = getOrCreateGtag();
    if (!gtag) {
        return;
    }

    const userProperties: Record<string, string> = {};

    if (context?.workspaceId) {
        userProperties.workspace_id = context.workspaceId;
    }

    window.__bustlyGaContext = {
        user_id: context?.userId || undefined,
        workspace_id: context?.workspaceId || undefined,
    };

    gtag('config', measurementId, {
        user_id: context?.userId || undefined,
        user_properties: Object.keys(userProperties).length > 0 ? userProperties : undefined,
    });
}

export function trackGoogleAnalyticsEvent(
    eventName: string,
    params: Record<string, unknown> = {}
) {
    const gtag = getOrCreateGtag();
    if (!gtag) {
        return;
    }

    gtag('event', eventName, {
        ...window.__bustlyGaContext,
        ...params,
    });
}
